import React, { useState } from "react";
import { Show } from "@refinedev/mui";
import {
  IResourceComponentsProps,
  useShow,
  useTranslate,
  useDelete,
  useNotification,
} from "@refinedev/core";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Grid,
  Avatar,
  Divider,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Person,
  LocationOn,
  MyLocation,
  Flag,
  AccessTime,
  Cancel,
  CheckCircle,
  Delete,
  Navigation,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { IJourney } from "./JourneyList";

interface JourneyWithUser extends IJourney {
  user: {
    id: number;
    fullname: string;
    email: string;
    phone?: string;
    profilePicture?: string;
    entrepriseId?: number;
    entreprise?: {
      id: number;
      nom: string;
    };
  };
  friends?: Array<{
    id: number;
    fullname: string;
    email: string;
    profilePicture?: string;
  }>;
}

export const JourneyShow: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();
  const { open: openNotification } = useNotification();
  const { mutate: deleteJourney } = useDelete();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { queryResult } = useShow<JourneyWithUser>({
    meta: {
      join: [
        { field: "user" },
        { field: "user.entreprise" },
        { field: "friends" },
      ],
    },
  });

  const { data, isLoading } = queryResult;
  const journey = data?.data;

  if (isLoading || !journey) {
    return <Show isLoading={isLoading} />;
  }

  const handleDelete = () => {
    deleteJourney(
      {
        resource: "journeys",
        id: journey.id,
      },
      {
        onSuccess: () => {
          openNotification?.({
            type: "success",
            message: translate("journeys.notifications.deleted"),
          });
          navigate("/journeys");
        },
        onError: () => {
          openNotification?.({
            type: "error",
            message: translate("journeys.notifications.deleteError"),
          });
        },
      }
    );
  };

  const formatCoordinates = (lat: number, lng: number) => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance.toFixed(2);
  };

  const totalDistance = calculateDistance(
    journey.originLatitude,
    journey.originLongitude,
    journey.destinationLatitude,
    journey.destinationLongitude
  );

  const traveledDistance = calculateDistance(
    journey.originLatitude,
    journey.originLongitude,
    journey.currentLatitude,
    journey.currentLongitude
  );

  const remainingDistance = calculateDistance(
    journey.currentLatitude,
    journey.currentLongitude,
    journey.destinationLatitude,
    journey.destinationLongitude
  );

  const progressPercentage = Math.min(
    100,
    Math.max(0, (parseFloat(traveledDistance) / parseFloat(totalDistance)) * 100)
  );

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            {translate("buttons.delete")}
          </Button>
        </>
      )}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={3}>
          {/* Status Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: journey.hasEnded
                  ? alpha(theme.palette.grey[500], 0.1)
                  : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(journey.hasEnded ? theme.palette.grey[500] : theme.palette.success.main, 0.2)}`,
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        background: alpha(journey.hasEnded ? theme.palette.grey[500] : theme.palette.success.main, 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Navigation sx={{ fontSize: 32, color: journey.hasEnded ? theme.palette.grey[500] : theme.palette.success.main }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        {translate("journeys.details.title")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {journey.id}
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={journey.hasEnded ? translate("journeys.status.ended") : translate("journeys.status.active")}
                    color={journey.hasEnded ? "default" : "success"}
                    icon={journey.hasEnded ? <Cancel /> : <CheckCircle />}
                    sx={{ fontSize: "1rem", py: 2 }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* User Information */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Person color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.fields.user")}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={journey.user?.profilePicture}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'primary.main',
                      }}
                    >
                      {journey.user?.fullname?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {journey.user?.fullname}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {journey.user?.email}
                      </Typography>
                      {journey.user?.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {journey.user?.phone}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  {journey.user?.entreprise && (
                    <Alert severity="info" icon={false}>
                      <Typography variant="body2" fontWeight={600}>
                        {translate("journeys.fields.company")}: {journey.user.entreprise.nom}
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Journey Progress */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Navigation color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.progress.title")}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.progress.total")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {totalDistance} km
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.progress.traveled")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {traveledDistance} km
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.progress.remaining")}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {remainingDistance} km
                      </Typography>
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Stack direction="row" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary">
                          {translate("journeys.progress.completion")}
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {progressPercentage.toFixed(1)}%
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${progressPercentage}%`,
                            height: '100%',
                            bgcolor: 'success.main',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Location Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack spacing={3}>
                  <Typography variant="h6" fontWeight={600}>
                    {translate("journeys.locations.title")}
                  </Typography>
                  <Divider />

                  <Grid container spacing={2}>
                    {/* Origin */}
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MyLocation color="success" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            {translate("journeys.locations.origin")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {formatCoordinates(journey.originLatitude, journey.originLongitude)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LocationOn />}
                          onClick={() =>
                            window.open(
                              `https://www.google.com/maps?q=${journey.originLatitude},${journey.originLongitude}`,
                              "_blank"
                            )
                          }
                        >
                          {translate("journeys.locations.viewOnMap")}
                        </Button>
                      </Stack>
                    </Grid>

                    {/* Current */}
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocationOn color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            {translate("journeys.locations.current")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {formatCoordinates(journey.currentLatitude, journey.currentLongitude)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LocationOn />}
                          onClick={() =>
                            window.open(
                              `https://www.google.com/maps?q=${journey.currentLatitude},${journey.currentLongitude}`,
                              "_blank"
                            )
                          }
                        >
                          {translate("journeys.locations.viewOnMap")}
                        </Button>
                      </Stack>
                    </Grid>

                    {/* Destination */}
                    <Grid item xs={12} md={4}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Flag color="error" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            {translate("journeys.locations.destination")}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {formatCoordinates(journey.destinationLatitude, journey.destinationLongitude)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LocationOn />}
                          onClick={() =>
                            window.open(
                              `https://www.google.com/maps?q=${journey.destinationLatitude},${journey.destinationLongitude}`,
                              "_blank"
                            )
                          }
                        >
                          {translate("journeys.locations.viewOnMap")}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Full Route Map Link */}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Navigation />}
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps/dir/${journey.originLatitude},${journey.originLongitude}/${journey.destinationLatitude},${journey.destinationLongitude}`,
                        "_blank"
                      )
                    }
                  >
                    {translate("journeys.locations.viewFullRoute")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Timestamps */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.timestamps.title")}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.timestamps.started")}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(journey.createdAt).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.timestamps.lastUpdate")}
                      </Typography>
                      <Typography variant="body2">
                        {journey.lastHeartbeat
                          ? new Date(journey.lastHeartbeat).toLocaleString()
                          : "-"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {translate("journeys.timestamps.updated")}
                      </Typography>
                      <Typography variant="body2">
                        {new Date(journey.updatedAt).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Shared With */}
          {journey.friends && journey.friends.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={600}>
                      {translate("journeys.sharedWith.title")} ({journey.friends.length})
                    </Typography>
                    <Divider />
                    <Stack spacing={1}>
                      {journey.friends.map((friend) => (
                        <Stack key={friend.id} direction="row" spacing={2} alignItems="center">
                          <Avatar
                            src={friend.profilePicture}
                            sx={{ width: 32, height: 32 }}
                          >
                            {friend.fullname?.[0]?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {friend.fullname}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {friend.email}
                            </Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {translate("journeys.delete.title")}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("journeys.delete.warning")}
          </Alert>
          <Typography>
            {translate("journeys.delete.confirmation")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {translate("buttons.cancel")}
          </Button>
          <Button
            onClick={() => {
              handleDelete();
              setDeleteDialogOpen(false);
            }}
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            {translate("buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Show>
  );
};
